// R1b-β — Authority Matrix tab body.
// Rehomed from /admin/authority. Preserves rule CRUD + "who can approve"
// evaluator. Every mutating call still lands in the audit ledger via the
// existing /api/authority/* endpoints.
import React, { useEffect, useState } from "react";
import EmptyState from "../../../components/EmptyState";
import { api, formatApiError } from "../../../lib/api";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Card, CardContent } from "../../../components/ui/card";
import { toast } from "sonner";
import { ScrollText, Plus, Trash2, Lock, Users, ArrowUpCircle } from "lucide-react";
import StatusChip from "../../../components/StatusChip";

const CAPABILITIES = ["read","create","edit","approve","reject","execute","publish"];

export default function AuthorityTab() {
  const [rules, setRules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    subject_type: "role", subject_id: "PROJECT_MANAGER",
    action_type: "approve_change_order", dollar_min: "", dollar_max: "",
    state: "", capability: "approve",
  });
  const [evalForm, setEvalForm] = useState({ action_type: "approve_change_order", dollar_amount: "5000" });
  const [evalResult, setEvalResult] = useState(null);

  const load = async () => {
    const [r, ro] = await Promise.all([api.get("/authority/rules"), api.get("/roles")]);
    setRules(r.data); setRoles(ro.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/authority/rules", {
        subject_type: form.subject_type,
        subject_id: form.subject_id,
        scope: {
          action_type: form.action_type,
          dollar_min: form.dollar_min === "" ? null : parseFloat(form.dollar_min),
          dollar_max: form.dollar_max === "" ? null : parseFloat(form.dollar_max),
          state: form.state || null,
        },
        capability: form.capability,
      });
      toast.success("Rule created — audit-logged");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete rule? This action is audit-logged.")) return;
    try { await api.delete(`/authority/rules/${id}`); load(); toast.success("Deleted (audit-logged)"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const evaluate = async () => {
    try {
      const r = await api.post("/authority/evaluate", {
        action_type: evalForm.action_type,
        attrs: { dollar_amount: parseFloat(evalForm.dollar_amount || "0") },
      });
      setEvalResult(r.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="authority-tab-body">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <div className="font-display font-semibold text-erf-dark text-lg">Authority Matrix</div>
          <div className="text-xs font-nav text-erf-dark/60 mt-0.5" data-testid="authority-count">{rules.length} rule(s) · every write audit-logged</div>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="authority-add-btn">
              <Plus className="w-4 h-4 mr-1" /> New rule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-erf-cream max-w-lg">
            <DialogHeader><DialogTitle className="font-display">New authority rule</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase font-nav">Subject type</Label>
                  <Select value={form.subject_type} onValueChange={(v) => setForm({...form, subject_type: v})}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="role">Role</SelectItem><SelectItem value="user">User</SelectItem></SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">Subject id / role key</Label>
                  {form.subject_type === "role" ? (
                    <Select value={form.subject_id} onValueChange={(v) => setForm({...form, subject_id: v})}>
                      <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                      <SelectContent>{roles.map(r => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input className="mt-1 font-mono" value={form.subject_id} onChange={(e)=>setForm({...form, subject_id: e.target.value})} />
                  )}
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Action type</Label>
                <Input className="mt-1 font-mono" value={form.action_type} onChange={(e)=>setForm({...form, action_type: e.target.value})} data-testid="authority-action-type" />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs uppercase font-nav">$ min</Label>
                  <Input type="number" className="mt-1 font-mono" value={form.dollar_min} onChange={(e)=>setForm({...form, dollar_min: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">$ max</Label>
                  <Input type="number" className="mt-1 font-mono" value={form.dollar_max} onChange={(e)=>setForm({...form, dollar_max: e.target.value})} />
                </div>
                <div>
                  <Label className="text-xs uppercase font-nav">State</Label>
                  <Input maxLength={2} placeholder="PA" className="mt-1 font-mono uppercase" value={form.state} onChange={(e)=>setForm({...form, state: e.target.value.toUpperCase()})} />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase font-nav">Capability</Label>
                <Select value={form.capability} onValueChange={(v) => setForm({...form, capability: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>{CAPABILITIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button type="submit" className="w-full bg-erf-gold text-erf-dark hover:bg-erf-goldHover" data-testid="authority-submit-rule">Create rule</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 bg-white border border-erf-borderCream rounded-md overflow-x-auto">
          <table className="w-full erf-table">
            <thead className="bg-erf-creamSecondary">
              <tr>
                <th className="text-left px-3 py-2">Subject</th>
                <th className="text-left px-3 py-2">Action</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Cap.</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rules.length === 0 && (
                <tr><td colSpan={5} className="p-0">
                  <EmptyState icon={ScrollText} title="No authority rules yet" subtitle="Add a rule to delegate approval authority (e.g. PM approves up to $10k)." />
                </td></tr>
              )}
              {rules.map((r) => (
                <tr key={r.id} className="border-t border-erf-borderCream/60" data-testid={`authority-rule-${r.id}`}>
                  <td className="px-3 py-2 mono">{r.subject_type}:{r.subject_id}</td>
                  <td className="px-3 py-2 font-nav">{r.scope?.action_type}</td>
                  <td className="px-3 py-2 mono text-erf-dark/70 text-[11px]">
                    {r.scope?.dollar_min != null && `$${r.scope.dollar_min}`}
                    {r.scope?.dollar_max != null && `→$${r.scope.dollar_max}`}
                    {r.scope?.state && ` · ${r.scope.state}`}
                  </td>
                  <td className="px-3 py-2 mono">{r.capability}</td>
                  <td className="px-3 py-2 text-right">
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-red-700 h-8"
                      onClick={() => del(r.id)}
                      data-testid={`authority-delete-${r.id}`}
                      aria-label={`Delete rule for ${r.subject_type} ${r.subject_id}, action ${r.scope?.action_type}`}
                      title="Delete this rule (audit-logged)"
                    >
                      <Trash2 className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Card className="border-erf-borderCream">
          <CardContent className="p-4 space-y-3">
            <div className="font-display font-semibold text-erf-dark">Who can approve?</div>
            <div>
              <Label htmlFor="who-can-action" className="text-xs uppercase font-nav">Action type</Label>
              <Input id="who-can-action" className="mt-1 h-11 font-mono" value={evalForm.action_type} onChange={(e)=>setEvalForm({...evalForm, action_type: e.target.value})} data-testid="who-can-action" aria-label="Action type to evaluate" />
            </div>
            <div>
              <Label htmlFor="who-can-amount" className="text-xs uppercase font-nav">Dollar amount</Label>
              <Input id="who-can-amount" type="number" className="mt-1 h-11 font-mono" value={evalForm.dollar_amount} onChange={(e)=>setEvalForm({...evalForm, dollar_amount: e.target.value})} data-testid="who-can-amount" aria-label="Dollar amount to evaluate against rules" />
            </div>
            <Button onClick={evaluate} className="w-full bg-erf-dark text-erf-cream" data-testid="who-can-evaluate-btn">Evaluate</Button>
            {evalResult && (
              <div className="mt-2 space-y-1" data-testid="who-can-result">
                <div className="text-xs font-nav text-erf-dark/70 uppercase tracking-wider">Eligible ({evalResult.eligible.length})</div>
                {evalResult.eligible.length === 0 && <div className="text-xs mono text-erf-dark/60">No matching rule — falls back to Owner.</div>}
                {evalResult.eligible.map((r) => (
                  <div key={r.id} className="mono text-[11px] text-erf-dark bg-erf-creamSecondary rounded-sm px-2 py-1">
                    {r.subject_type}:{r.subject_id} · {r.capability}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* R1b-β Step 3 Deliverable F — honest phase_n cards for escalation
          and substitute approvers. No fake data; endpoints intentionally
          absent until R1b-γ. */}
      <div className="grid gap-6 lg:grid-cols-2 mt-8" data-testid="authority-phase-cards">
        <Card className="border-erf-borderCream bg-erf-cream" data-testid="authority-escalation-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-erf-gold/10 border border-erf-gold/40 flex items-center justify-center shrink-0">
                  <ArrowUpCircle className="w-5 h-5 text-erf-goldHover" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">Not Implemented</div>
                  <div className="font-display font-semibold text-erf-dark mt-0.5">Escalation policies</div>
                  <p className="text-xs font-nav text-erf-dark/70 mt-1 leading-relaxed">
                    Time-based escalation when an approver does not decide within a defined window
                    (e.g. auto-route to the next-band approver after 24h). Not implemented.
                  </p>
                </div>
              </div>
              <StatusChip status="not_implemented" />
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-erf-dark/50">
              <Lock className="w-3 h-3" /> Truthful placeholder — no fake data
            </div>
          </CardContent>
        </Card>

        <Card className="border-erf-borderCream bg-erf-cream" data-testid="authority-substitute-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-sm bg-erf-gold/10 border border-erf-gold/40 flex items-center justify-center shrink-0">
                  <Users className="w-5 h-5 text-erf-goldHover" strokeWidth={1.8} />
                </div>
                <div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-erf-dark/50">Not Implemented</div>
                  <div className="font-display font-semibold text-erf-dark mt-0.5">Substitute approvers</div>
                  <p className="text-xs font-nav text-erf-dark/70 mt-1 leading-relaxed">
                    Nominate a stand-in approver for planned absences (leave, delegation windows).
                    Not implemented. When built, every substitution must be audit-logged and bounded to a start/end date.
                  </p>
                </div>
              </div>
              <StatusChip status="not_implemented" />
            </div>
            <div className="mt-3 flex items-center gap-2 text-[11px] font-mono text-erf-dark/50">
              <Lock className="w-3 h-3" /> Truthful placeholder — no fake data
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
